<template>
  <div ref="editorElement">
    <EditorVue
      v-if="showEditor"
      v-model="content"
      :init="editorConfig"
      :inline="true"
      :plugins="plugins"
      tag-name="div"
      class="mjeditor"
      :class="{
        hidden: !isLoaded,
      }"
      :style="editorStyle"
      style="cursor: auto; outline: none"
      @init="onInit"
      @click="onClick"
      @focus="onClick"
      @blur.prevent="onBlur"
      @change="onChange"
    ></EditorVue>
    <div v-if="!isLoaded" :style="editorStyle" v-html="content"></div>
  </div>
</template>

<script lang="ts" setup>
  import EditorVue from "@tinymce/tinymce-vue";

  import {
    RawEditorOptions,
    Editor,
  } from "../../../public/assets/tinymce/tinymce";
  import IMergeTag from "types/MergeTag/IMergeTag";
  import IMergeTagGroup from "types/MergeTag/IMergeTagGroup";
  import { useFontStore } from "@/stores/fonts";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useTinyConfigStore } from "@/stores/tinyConfig";
  import { useColorsStore } from "@/stores/colors";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useResizeColumnsStore } from "@/stores/resizeColumnsStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import combineLinkLists from "@/utils/combineLinkLists";
  import {
    flattenMergeTags,
    isPlainMergeTag,
    mapMergeTagTree,
  } from "@/utils/mergeTags";
  import type { IMergeTagArray } from "@/schemas/mergeTags/mergeTagsSchema";
  import type { ILanguage } from "@/schemas/language/languageSchema";
  import { getCopiedElement } from "@topol/utils";
  import { useI18n } from "vue-i18n";
  import { StyleValue, ref, Ref, computed, watch } from "vue";

  interface Props {
    modelValue: string;
    editorStyle: StyleValue | Record<string, any>;
    toolbar?: string | string[];
    rootBlock?: string;
    hasFont?: boolean;
    productSize?: boolean;
    buttonSize?: boolean;
    disableImage?: boolean;
    isText?: boolean;
    selectAll?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: "",
    toolbar: "",
    rootBlock: "",
    hasFont: false,
    productSize: false,
    buttonSize: false,
    disableImage: false,
    isText: false,
    selectAll: false,
  });

  const emits = defineEmits([
    "update:modelValue",
    "onFontSizeChanged",
    "on-change",
    "on-init",
    "onBlur",
  ]);

  const focus = () => {
    if (editor.value) {
      editor.value.focus();
    }
  };

  const editor: Ref<Editor | null> = ref(null);
  const isLoaded = ref(false);
  const editorElement = ref();

  const autocompleteMergeTags: Ref<any[]> = ref([]);

  const { t } = useI18n();
  const fontStore = useFontStore();
  const mergeTagsStore = useMergeTagsStore();
  const tinyConfigStore = useTinyConfigStore();
  const colorsStore = useColorsStore();
  const generalStore = useGeneralStore();
  const resizeColumnsStore = useResizeColumnsStore();
  const mjmlStore = useMjmlStore();

  const isChanging = computed(() => {
    return (
      resizeColumnsStore.isResizing ||
      generalStore.colorSelecting ||
      mjmlStore.sectionReordering
    );
  });

  const showEditor = ref(!isChanging.value);
  let hideOnceInitialized = false;

  function hideEditor() {
    hideOnceInitialized = false;
    showEditor.value = false;
    isLoaded.value = false;
    editor.value = null;
  }

  watch(isChanging, (changing) => {
    if (!changing) {
      hideOnceInitialized = false;
      showEditor.value = true;
      return;
    }
    if (isLoaded.value) {
      hideEditor();
    } else {
      hideOnceInitialized = true;
    }
  });

  const content = computed({
    get() {
      return props.modelValue;
    },
    set(val: string) {
      if (props.disableImage && val.includes("<img")) {
        return;
      }

      // Remove `draggable="false"` that accours in the tiny mce anchor tags and images
      val = val.replace(/draggable="false"\s*/g, "");

      emits("update:modelValue", val);
    },
  });

  const selectAllToWatch = computed(() => {
    return props.selectAll;
  });

  const toolbarOptions = computed(() => {
    if (props.selectAll) {
      return false;
    }
    if (props.toolbar !== "") {
      return props.toolbar;
    }
    return [
      "blocks fontfamily fontsizeinput lineheight bold italic underline strikethrough  removeformat nonbreaking| alignleft aligncenter alignright   alignjustify link numlist bullist forecolor backcolor  mergefields emoticons code more",
    ];
  });

  const mergeTags = computed((): Array<IMergeTagGroup | IMergeTag> => {
    const returnData: Array<IMergeTagGroup | IMergeTag> = [
      {
        name: t("editor.personalisations"),
        items: [
          {
            value: "*|NAME|*",
            text: t("editor.name"),
            label: "Name",
          },
          {
            value: "*|SURNAME|*",
            text: t("editor.surname"),
            label: "Surname",
          },
          {
            value: "*|FULLNAME|*",
            text: t("editor.fullname"),
            label: "Fullname",
          },
          {
            value: "*|EMAIL|*",
            text: t("editor.email"),
            label: "E-mail",
          },
          {
            value: "*|CALLNAME|*",
            text: t("editor.call_name"),
            label: "Call name",
          },
          {
            value: "*|CALLSURNAME|*",
            text: t("editor.call_surname"),
            label: "Call surname",
          },
        ],
      },
      {
        name: t("editor.links"),
        items: [
          {
            value: "*|LOGOUTLINK|*",
            text: t("editor.logout_link"),
            label: "Logout link",
          },
          {
            value: "*|ONLINELINK|*",
            text: t("editor.online_version_link"),
            label: "Online version link",
          },
        ],
      },
    ];

    const mergeTags = mergeTagsStore.mergeTagsWithoutArray;

    return mergeTags.length > 0 ? mergeTags : returnData;
  });

  const editorConfig = computed(() => {
    let conf: RawEditorOptions = {};

    // The customer's tinyConfig is merged in further down, so their link_list
    // has to be read from the store to decide the dialog's field label here.
    const { linkList, mergeTagsOnly } = combineLinkLists(
      (tinyConfigStore.tinyConfig as RawEditorOptions | undefined)?.link_list,
      mergeTagsStore.mergeTagLinkList,
      t("editor.personalisations")
    );

    conf = {
      font_family_formats: getFonts(),
      font_size_formats: getFontSizes(),
      font_size_input_default_unit: "px",
      block_formats: `${t("forms.settings_paragraph")}=p; ${t(
        "forms.settings_heading1"
      )}=h1; ${t("forms.settings_heading2")}=h2; ${t(
        "forms.settings_heading3"
      )}=h3`,
      menubar: false,
      // newline_behavior: "default",
      contextmenu: false,
      link_default_protocol: "https",
      toolbar_mode: "floating",
      toolbar: toolbarOptions.value,
      nonbreaking_wrap: true,
      link_default_target: "_blank",
      toolbar_groups: {
        more: {
          icon: "dots",
          tooltip: "More options",
          items: "table charmap pastetext ltr rtl",
        },

        more_button: {
          icon: "dots",
          tooltip: "More options",
          items: "mergefields code charmap emoticons ltr rtl",
        },
      },
      table_toolbar:
        "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
      table_default_styles: {
        "border-collapse": "collapse",
        width: "100%",
        "font-size": "11px",
      },
      language: getLanguageName(),
      directionality: "ltr",
      inline_boundaries: false, //removes background when link is clicked
      resize: false,
      width: 670,
      entity_encoding: "raw",
      min_width: 400,
      forced_root_block: props.rootBlock !== "" ? props.rootBlock : "div", //removes root tag from default code when root block is not set
      forced_root_block_attrs: {
        style: props.hasFont ? "font-family: inherit;" : "",
      },
      paste_as_text: true,
      body_class: "left",
      paste_data_images: false,
      paste_block_drop: true,
      line_height_formats: "1 1.1 1.2 1.3 1.4 1.5 1.6 1.7 1.8 1.9 2",

      //ignore * as italic format because of ECM merge tags
      text_patterns: false,

      setup: (editor) => {
        // Overrides layered on top of the TinyMCE language pack. Only two are
        // load-bearing: "URL", which no pack ships an entry for, and "Link list",
        // which we relabel because it says nothing about the field holding merge
        // tags — but only when merge tags are all the field holds. A customer
        // link_list keeps the pack's label, with merge tags nested inside it as
        // one named group. The rest just pin our own wording over the pack's;
        // lookups are case-insensitive (addI18n lowercases keys, translate
        // lowercases the text), so the vendored link plugin's "Insert/Edit Link"
        // casing is irrelevant and the pack would otherwise cover them.
        //
        // This MUST stay inside an "init" handler rather than running directly in
        // setup(). TinyMCE only fetches langs/<code>.js when I18n.hasCode(<code>)
        // is false, and hasCode() merely asks whether a dictionary object exists
        // for that code - not whether it holds anything. setup() runs at the end
        // of the Editor constructor, before render() reaches that check, so an
        // addI18n() call here would create the dictionary early, convince TinyMCE
        // the pack was already loaded and stop it from ever requesting the file,
        // leaving the whole editor UI in English. "init" fires once the script
        // queue has resolved, so the pack is in place by then.
        //
        // Resolve the code here rather than in the handler: the handler runs a
        // network round-trip later, and updateOptions() can change the language
        // in between. Registering under a code the editor did not render with
        // would drop these overrides and re-arm the bug for later editors.
        const languagePack = getLanguageName();
        editor.on("init", () => {
          editor.editorManager.addI18n(languagePack, {
            "Insert/Edit Link": t("editor.link_dialog.title"),
            URL: t("editor.link_dialog.url"),
            "Text to display": t("editor.link_dialog.text_to_display"),
            Title: t("editor.link_dialog.link_title"),
            ...(mergeTagsOnly
              ? { "Link list": t("editor.personalisations") }
              : {}),
          });
        });
        editor.ui.registry.addMenuButton("mergefields", {
          text: t("editor.personalisations"),
          fetch: (callback) => {
            const getSubMenuItemsRec = (item: any) => {
              if (item.items) {
                return {
                  type: "nestedmenuitem",
                  text: item.name,
                  getSubmenuItems: function () {
                    return item.items.map((subItem: IMergeTag) => {
                      return getSubMenuItemsRec(subItem);
                    });
                  },
                };
              } else {
                return {
                  type: "menuitem",
                  text:
                    item.text.toLowerCase() === "code"
                      ? item.text + " "
                      : item.text,
                  onAction: function () {
                    if (
                      mergeTagsStore.smartMergeTags.syntax?.start === "<" ||
                      mergeTagsStore.smartMergeTags.syntax?.end === ">"
                    ) {
                      editor.insertContent(
                        item.value.replace("<", "&lt;").replace(">", "&gt;")
                      );
                    } else {
                      editor.insertContent(item.value);
                    }
                  },
                };
              }
            };

            callback(
              //@ts-expect-error
              mergeTags.value.map((item: any) => getSubMenuItemsRec(item))
            );
          },
        });

        editor.ui.registry.addAutocompleter("merge-tags", {
          trigger: mergeTagsStore.smartMergeTags.syntax?.start,
          minChars: 1,

          onAction: (autocompleteApi, rng, value) => {
            editor.selection.setRng(rng);
            editor.insertContent(value);
            autocompleteApi.hide();
          },

          fetch: (pattern: string) => {
            return new Promise((resolve) => {
              resolve(filterAutocompleteMergeTags(pattern));
            });
          },
        });

        editor.ui.registry.addIcon(
          "dots",
          `<svg style="color: #222f3e; width: 20px; height: 20px;" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
          </svg>`
        );
        editor.on("paste", (e) => {
          if (!e.clipboardData) {
            return;
          }
          const data = e.clipboardData.getData("text");

          if (
            data.includes("<img") ||
            (data.includes("{") && data.includes("}") && getCopiedElement(data))
          ) {
            e.preventDefault();
          }
        });
      },
      paste_preprocess: (plugin: any, args: any) => {
        if (args.content.toString().includes("<img")) {
          args.content = "";
        }
      },
    };

    if (colorsStore.colors) {
      const colors = colorsStore.colors;

      if (colors != null && colors.length > 0) {
        const mappedColors = [];

        for (const color of colors) {
          mappedColors.push(color);
          mappedColors.push("Brand Color");
        }

        conf.color_map = mappedColors;
      }
    }

    if (tinyConfigStore.tinyConfig !== undefined) {
      const tinyConf = tinyConfigStore.tinyConfig;
      conf = Object.assign(conf, tinyConf);

      if (props.selectAll) {
        conf.toolbar = false;
      } else if (props.toolbar) {
        conf.toolbar = props.toolbar;
      }
    }

    if (linkList !== undefined) {
      conf.link_list = linkList;
    }

    if (mergeTagsStore.smartMergeTags.enabled) {
      const pluginUrl =
        getStartUrl() + "plugins/visualmergetags/visual-merge-tags.js";

      conf.external_plugins = {
        ...conf.external_plugins,
        visualMergeTags: pluginUrl,
      };

      conf.merge_tags_patterns = mergeTagsStore.smartMergeTags.patterns;
      conf.merge_tags = prepareMergeTagsForListBox(
        mergeTagsStore.mergeTagsWithoutArray
      );

      conf.merge_tags_syntax = {
        start: mergeTagsStore.smartMergeTags.syntax?.start,
        end: mergeTagsStore.smartMergeTags.syntax?.end,
      };
      conf.merge_tag_title = t("editor.personalisation");
    }

    return conf;
  });

  type ListBoxItem =
    | { text: string; value: string }
    | { text: string; items: ListBoxItem[] };

  /**
   * Shape the visual-merge-tags plugin's listbox expects: nested on `items`,
   * with the syntax delimiters stripped — the plugin re-adds them on insert.
   */
  function prepareMergeTagsForListBox(mt: IMergeTagArray): ListBoxItem[] {
    const syntax = mergeTagsStore.MTSyntax;

    return mapMergeTagTree<ListBoxItem>(mt, {
      mapLeaf: (mergeTag) =>
        isPlainMergeTag(mergeTag.value)
          ? {
              text: mergeTag.text,
              value: mergeTag.value
                .replace(syntax.start, "")
                .replace(syntax.end, ""),
            }
          : null,
      mapGroup: (text, items) => ({ text, items }),
    });
  }

  const plugins = computed(() => {
    return [
      "autolink",
      "link",
      "lists",
      "advlist",
      "code",
      "charmap",
      "nonbreaking",
      "table",
      "emoticons",
      "directionality",
    ];
  });

  const onBlur = () => {
    emits("onBlur");

    if (props.isText) {
      window.removeEventListener("scroll", stickToolbar);
    }
  };

  const getMergeTags = () => {
    if (autocompleteMergeTags.value.length !== 0) {
      return autocompleteMergeTags.value;
    }

    const syntax = mergeTagsStore.MTSyntax;

    autocompleteMergeTags.value = flattenMergeTags(
      mergeTagsStore.mergeTagsWithoutArray
    ).filter(
      (mergeTag) =>
        mergeTag.value.startsWith(syntax.start) &&
        mergeTag.value.endsWith(syntax.end) &&
        // A conditional block is not something the autocompleter can insert.
        !mergeTag.value.startsWith("|*IF:")
    );

    return autocompleteMergeTags.value;
  };

  function filterAutocompleteMergeTags(pattern: string): IMergeTag[] {
    const items = getMergeTags();
    if (pattern === "") {
      return items;
    }
    return items.filter((item) =>
      item.value.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  const onChange = (e: any) => {
    if (e && e.level !== undefined && e.level.content !== undefined) {
      const heighestFontSize = getHighestFontSizeOfHTMLString(e.level.content);

      if (heighestFontSize !== undefined) {
        emits("onFontSizeChanged", heighestFontSize);
      }
    }

    emits("on-change", e);
  };

  const getHighestFontSizeOfHTMLString = (htmlString: string) => {
    const delimeter = /font-size: (.*?)px;/;
    const parsed = htmlString.split(delimeter);
    const values = parsed.map((v) => {
      try {
        const parsed = parseInt(v);

        if (!isNaN(parsed)) {
          return parsed;
        }
      } catch (err) {
        //comment to prevent eslint error
      }
    });
    const filtered = values.filter((v) => v !== undefined) as number[];

    if (filtered.length > 0) {
      return Math.max(...filtered);
    }
  };

  const onInit = (event: any, editorP: Editor) => {
    emits("on-init", event);

    editor.value = editorP;

    isLoaded.value = true;
    if (hideOnceInitialized && isChanging.value) {
      // Teardown was requested while this instance was still initializing.
      hideEditor();
      return;
    }
    if (props.selectAll) {
      editor.value.selection.select(editor.value.getBody(), true);
    }
  };

  const getFontSizes = () => {
    const sizes = fontStore.fontSizes;

    //covert size format to suit TinyMCE config style

    let tinyCompatibleSizes = "";

    for (const size of sizes) {
      tinyCompatibleSizes += `${size}px `;
    }

    return tinyCompatibleSizes;
  };

  const getFonts = () => {
    const fonts = fontStore.fonts;

    let fontsString = "";

    for (const font of fonts) {
      fontsString += `${font.label}=${font.style};`;
    }

    return fontsString;
  };

  // Editor language codes that differ from the filename of the TinyMCE language
  // pack in public/assets/tinymce/langs. Every value here must match a pack that
  // actually ships - TinyMCE falls back to English *silently* when the pack 404s,
  // which reverts the whole editor UI (link dialog included) to English.
  const TINYMCE_LANGUAGE_PACKS: Partial<Record<ILanguage, string>> = {
    zh: "zh-Hans",
    fr: "fr_FR",
    he: "he_IL",
    ko: "ko_KR",
    pt: "pt_BR",
    sv: "sv_SE",
  };

  const getLanguageName = (): string => {
    const language = generalStore.language;

    return TINYMCE_LANGUAGE_PACKS[language] ?? language;
  };

  const onClick = () => {
    const el = document.querySelector("body");
    const toolbars = document.querySelectorAll(
      ".tox-tinymce-inline"
    ) as unknown as HTMLElement[];

    const controlPanel = document.getElementById("controlPanel");

    if (el === null || !editorElement.value) {
      return;
    }

    const controlPanelWidth = controlPanel ? controlPanel.offsetWidth : 381;
    const toolbarWidth = props.buttonSize ? 450 : props.productSize ? 307 : 670;
    const worksSpaceWidth =
      window.innerWidth -
      (el.classList.contains("right") ? controlPanelWidth : 0);
    const leftLimit = el.classList.contains("right") ? 0 : controlPanelWidth;
    const rightLimit = el.classList.contains("right") ? controlPanelWidth : 0;

    toolbars.forEach((tb) => {
      if (tb.style.display !== "flex") {
        return;
      }

      if (tb.offsetWidth < toolbarWidth) {
        //@ts-expect-error
        tb.style["min-width"] = toolbarWidth + "px";
      }

      const fixedPos = generalStore.textEditorToolbarBottomPosition;
      if (fixedPos) {
        tb.style.position = "fixed";
        tb.style.top = "auto";
        tb.style.bottom = "5px";
        if (fixedPos === "left") {
          tb.style.left = leftLimit + 5 + "px";
          tb.style.right = "auto";
        } else if (fixedPos === "right") {
          tb.style.right = rightLimit + 5 + "px";
          tb.style.left = "auto";
        } else {
          tb.style.left =
            leftLimit +
            (worksSpaceWidth - leftLimit) / 2 -
            toolbarWidth / 2 +
            "px";
          tb.style.right = "auto";
        }
        return;
      }
      if (worksSpaceWidth <= tb.offsetLeft + toolbarWidth) {
        tb.style.left = worksSpaceWidth - toolbarWidth + "px";
      }

      const centerDistance =
        tb.offsetLeft -
        (editorElement.value.getBoundingClientRect().left -
          (tb.offsetWidth - editorElement.value.clientWidth) / 2);

      if (centerDistance > 0 && tb.offsetLeft - centerDistance > leftLimit) {
        tb.style.setProperty("--tox-offset", -centerDistance + "px");
      } else if (centerDistance > 0) {
        tb.style.setProperty(
          "--tox-offset",
          -(centerDistance - (leftLimit - (tb.offsetLeft - centerDistance))) +
            "px"
        );
      } else {
        tb.style.setProperty("--tox-offset", "0%");
      }

      tb.style.setProperty("--toy-offset", "0px");
    });

    if (props.isText) {
      stickToolbar();
      window.addEventListener("scroll", stickToolbar);
    }
  };

  function stickToolbar() {
    if (!editorElement.value) {
      return;
    }

    const toolbars = document.querySelectorAll(
      ".tox-tinymce-inline"
    ) as unknown as HTMLElement[];
    toolbars.forEach((toolbar) => {
      if (!toolbar || toolbar.style.display !== "flex") {
        return;
      }
      const toolbarTop = toolbar.offsetTop;
      const scrollTop = window.scrollY;
      const bodyRect = document.body.getBoundingClientRect(),
        elemRect = editorElement.value.getBoundingClientRect(),
        offset = elemRect.top - bodyRect.top;
      const editorBottom = offset + editorElement.value.clientHeight;
      const toolbarBottom =
        scrollTop -
        toolbarTop +
        (toolbar.querySelector(".tox-editor-header")?.clientHeight || 82) +
        20;

      if (scrollTop > toolbarTop && editorBottom > toolbarBottom) {
        toolbar.style.setProperty(
          "--toy-offset",
          scrollTop - toolbarTop + "px"
        );
      } else if (editorBottom < toolbarBottom) {
        return;
      } else {
        toolbar.style.setProperty("--toy-offset", "0px");
      }
    });
  }

  function getStartUrl() {
    if (import.meta.env.DEV || import.meta.env.MODE === "testing") {
      return "";
    }

    if (import.meta.env.VITE_APP_PREVIEW_APP) {
      return `https://d10h8ga60f7b91.cloudfront.net${
        import.meta.env.VITE_APP_AWS_FOLDER
      }/assets/tinymce/`;
    }

    return `${import.meta.env.VITE_APP_DOMAIN}/assets/tinymce/`;
  }

  watch(selectAllToWatch, (val) => {
    if (val) {
      editor.value?.selection.select(editor.value.getBody(), true);
    } else {
      editor.value?.selection.collapse();
    }
  });

  defineExpose({
    focus,
  });
</script>

<style lang="scss">
  @import "../../css/helpers";

  .mce-content-body td {
    font-size: 11px;
    font-family: Helvetica, Arial, sans-serif;
  }

  .tox {
    z-index: 1600 !important;
  }

  .tox-tinymce-aux {
    z-index: 1601 !important;
  }

  .tox-dialog-wrap__backdrop {
    z-index: 1602 !important;
  }

  .tox-dialog {
    z-index: 1603 !important;
  }

  .tox-tinymce-inline {
    transform: translate(var(--tox-offset), var(--toy-offset)) !important;
    opacity: var(--tox-opactity) !important;
    // position: sticky !important;
  }

  .tox .tox-editor-header {
    left: var(--tox-padding) !important;
    opacity: 1 !important;
    visibility: var(--tox-visible) !important;
  }

  .tox-editor-dock-transition {
    opacity: 1 !important;
  }

  /* Change color of the headins in TinyMCE toolbar*/
  .tox-collection__item-label > * {
    color: rgb(60, 60, 60) !important;
  }

  .topol-special-merge-tag {
    cursor: pointer;
    outline: 1px dashed var(--c-structure-1);
    background-color: inherit !important;
    color: inherit !important;
    font-size: inherit !important;
    font-family: inherit !important;
    font-weight: inherit !important;
    font-style: inherit !important;
    text-decoration: inherit !important;
  }

  .topol-special-merge-tag:hover {
    background-color: #907dff23 !important;
  }

  .topol-special-merge-tag:focus {
    outline: none !important;
    border: none !important;
  }
</style>
